import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const PaymentMethodContext = createContext();
 
export const usePaymentMethods = () => {
  const context = useContext(PaymentMethodContext);
  if (!context) {
    throw new Error('usePaymentMethods must be used within a PaymentMethodProvider');
  }
  return context;
};

export const PaymentMethodProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/payment-methods');

  const getPaymentMethods = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      setPaginate(true)
      let response = await apiInstance.call('payment-methods?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        if(filters?.active){
          if(filters?.active === true){
            query += 'active=1';
          }else{
            query += 'active=0';
          }
         
        }
        response = await apiInstance.call('payment-methods?'+query);  
      }
      
      if (response.status === 200) {
       // setPaginate(response.data.paginate)
        setPaymentMethods(response.data.paymentMethods || []);
       
        
        if(filters){
          return response.data.paymentMethods;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch payment methods information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, getToken,paginate]);

  const getPaymentMethodByIdApi = useCallback(async (id, filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`payment-methods/${id}/edit`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let paymentMethods = {'data':[response.data.paymentMethod]}
      setPaymentMethods(paymentMethods || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/payment-methods/edit/'+id))){
        if (getToken()) {
        
          
          await getPaymentMethodByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getPaymentMethodByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getPaymentMethods(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getPaymentMethods, searchParams, initialized,hideLoading,setApi]);

  const getPaymentMethodById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`payment-methods/${id}/edit`, 'GET', null, true, false);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.paymentMethod };
      } else {
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch payment method information'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch payment method information'
      });
      return { status: 500, error: error.message };
    }
  }, [handleApiError, getToken]);

  const addPaymentMethod = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(data));

      const response = await apiInstance.call('payment-methods', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the payment methods list
        await getPaymentMethods();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Payment Method'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create payment method'
      });
      return { status: 500, error: error.message };
    }
  };

  const updatePaymentMethod = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();
      
      // Append the data as JSON
      formData.append('data', JSON.stringify(updatedData));
       
      // Now send the request with formData only once
      const response = await apiInstance.call(`payment-methods/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the payment methods list
        await getPaymentMethods();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Payment Method'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update payment method'
      });
      return { status: 500, error: error.message };
    }
  };

  const deletePaymentMethod = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`payment-methods/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the payment methods list
        await getPaymentMethods();
        navigate(getRoutePath('/payment-methods'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete payment method'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete payment method'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <PaymentMethodContext.Provider
      value={{
        paymentMethods,
        addPaymentMethod,
        getPaymentMethodById,
        updatePaymentMethod,
        deletePaymentMethod,
        getPaymentMethods,
        formErrors,
        clearErrors,
        setPaginate,
        paginate
      }}
    >
      {children}
    </PaymentMethodContext.Provider>
  );
}; 